// ── BCHNostr — Video watch page ───────────────────────────────────
//
// The one place in the app where a video player is mounted. Two rules
// shape the whole file:
//
//   1. Playback comes from the EXTERNAL PROVIDER. We render either the
//      provider's own iframe or a plain <video src> pointed at their file.
//      BCHNostr never proxies, caches, re-encodes or stores the media.
//   2. The player is NOT mounted until the viewer presses Play. Until then
//      it's a thumbnail and a button — so no third-party frame, script or
//      cookie loads for a page the viewer merely opened, and there is no
//      possibility of autoplay.
//
// If the external video breaks, the BCHNostr page around it stays intact:
// the title, description, discussion, tips and boosts all keep working,
// and we say plainly that the video is unavailable.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Play, Rocket, MessageCircle, Share2, ExternalLink, Loader2,
  Send, Check, Heart, AlertTriangle, Eye, MousePointerClick, Video as VideoIcon,
  Pencil, Trash2, ListPlus, Star, Coins, Info, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile, useNostrQuery } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useBCHPrice } from '../hooks/useBCHPrice';
import {
  useVideo, useVideoDirectory, useVideoComments, useSingleVideoEngagement,
  useVideoBoosts, useVideoPublish, useVideoPlaylists,
} from '../hooks/useVideo';
import BCHTipButton from '../components/BCHTipButton';
import VideoTipControl from '../components/VideoTipControl';
import FollowButton from '../components/FollowButton';
import VideoBoostModal from '../components/VideoBoostModal';
import AddVideoModal from '../components/AddVideoModal';
import VideoCard, { PromotionBadge } from '../components/VideoCard';
import UserBadges from '../components/UserBadges';
import { CreatorSupportPanel } from './CreatorSupportPage';
import { VideoEmptyState } from './VideosPage';
import { timeAgo } from '../lib/utils';
import {
  VIDEO_COMMENT_KIND, VIDEO_COMMENT_TAG, VIDEO_KIND, TIP_RECEIPT_KIND,
  videoCoordinate, videoChannelPath, playlistPath, playlistDTag,
  formatPublished, formatCount, formatVideoDuration, videoCategoryLabel,
  recordVideoView, recordPlayClick, recordWatch, recordCategoryInterest,
  videoViewCount, playClickCount, relatedVideos, buildVideoShareText,
  videoProviderLabel,
  type BCHVideo, type VideoEngagement,
} from '../lib/video';

// ── Player ────────────────────────────────────────────────────────
//
// Deliberately lazy. The `activated` flag is the entire performance and
// privacy story: before it flips, this component renders an <img> and a
// button. Nothing from the provider is contacted.

function VideoPlayer({ video }: { video: BCHVideo }) {
  const [activated, setActivated] = useState(false);
  const [failed, setFailed] = useState(false);
  const countedRef = useRef(false);
  const playback = video.playback;

  const handleActivate = useCallback(() => {
    // A play CLICK, recorded once per page view. Explicitly NOT a "view" —
    // see the honesty note in the analytics panel below.
    if (!countedRef.current) {
      countedRef.current = true;
      recordPlayClick(video.pubkey, video.id);
    }
    setActivated(true);
  }, [video.pubkey, video.id]);

  // ── Unplayable: keep the page, link out ──
  if (!playback || playback.playback === 'link') {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="relative aspect-video bg-muted">
          {video.thumbnailUrl ? (
            <img src={video.thumbnailUrl} alt="" className="w-full h-full object-cover opacity-60" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <VideoIcon className="w-10 h-10 text-muted-foreground/30" />
            </div>
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 px-6 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
            <p className="text-sm font-semibold text-white">Can't play here</p>
            <p className="text-[11px] text-white/80 max-w-xs leading-relaxed">
              This video's host can't be embedded safely, so it plays on their site.
              Everything else on this page still works.
            </p>
            <a
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Watch on {videoProviderLabel(video)}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-black overflow-hidden">
      <div className="relative aspect-video">
        {!activated ? (
          // ── Poster + play button. No player, no third-party request. ──
          <button
            onClick={handleActivate}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play ${video.title}`}
          >
            {video.thumbnailUrl ? (
              <img
                src={video.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-emerald-900/20" />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
              <span className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform">
                <Play className="w-7 h-7 text-white ml-1" />
              </span>
            </span>
            {video.duration > 0 && (
              <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/80 text-white text-[11px] font-semibold tabular-nums">
                {formatVideoDuration(video.duration)}
              </span>
            )}
          </button>
        ) : playback.playback === 'iframe' ? (
          // The provider's own player. `allowFullScreen` for mobile
          // fullscreen; no `allow-same-origin` privileges are granted
          // beyond what the provider needs to play the video.
          <iframe
            src={playback.embedUrl}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
          />
        ) : (
          // A direct media file. Native controls give seeking, speed,
          // fullscreen, AirPlay and captions for free, and keep playback
          // strictly between the browser and the host.
          <video
            src={playback.fileUrl}
            poster={video.thumbnailUrl || undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full bg-black"
            onError={() => setFailed(true)}
          />
        )}
      </div>

      {failed && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-500/10 border-t border-amber-500/30">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Video unavailable.</strong> It couldn't be
            loaded from {videoProviderLabel(video)} — it may have been removed or made
            private by the creator. This page and its discussion are unaffected.{' '}
            <a href={video.url} target="_blank" rel="noopener noreferrer" className="underline">
              Try the original
            </a>.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Discussion ────────────────────────────────────────────────────
//
// NIP-22 comments (kind 1111) rooted on the video's addressable
// coordinate, so the thread is portable: any client implementing NIP-22
// addresses the same conversation, and nothing about the media is
// duplicated into Nostr.

function CommentRow({
  event, video, onReplyPosted,
}: {
  event: { id: string; pubkey: string; content: string; created_at: number; tags: string[][] };
  video: BCHVideo;
  onReplyPosted: () => void;
}) {
  const { profile } = useNostrProfile(event.pubkey);
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8);
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const handleReply = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    const content = draft.trim();
    if (!content || posting) return;
    setPosting(true);
    const coord = videoCoordinate(video.pubkey, video.id);
    try {
      await publish({
        kind: VIDEO_COMMENT_KIND,
        content,
        tags: [
          // Root scope (uppercase) stays the VIDEO, per NIP-22 — so a
          // nested reply still belongs to the same video thread.
          ['A', coord],
          ['K', String(VIDEO_KIND)],
          // Parent (lowercase) is the comment being replied to.
          ['e', event.id],
          ['k', String(VIDEO_COMMENT_KIND)],
          ['p', event.pubkey],
          ['t', VIDEO_COMMENT_TAG],
          ['alt', `Reply on the video "${video.title}"`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setDraft('');
      setReplying(false);
      onReplyPosted();
    } catch {
      // Keep the draft so the reply isn't lost.
    } finally {
      setPosting(false);
    }
  };

  const isReply = event.tags.some(t => t[0] === 'e');

  return (
    <div className={`flex items-start gap-2.5 px-4 py-3 border-b border-border ${isReply ? 'pl-10 bg-muted/20' : ''}`}>
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" loading="lazy" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10" />
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link to={`/profile/${event.pubkey}`} className="text-xs font-semibold hover:underline truncate">
            {name}
          </Link>
          <UserBadges pubkey={event.pubkey} size="sm" showTooltip={false} />
          {event.pubkey === video.pubkey && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-bold">
              Creator
            </span>
          )}
          <span className="text-[10px] text-muted-foreground">{timeAgo(event.created_at)}</span>
        </div>
        {/* Plain text only — never dangerouslySetInnerHTML on user content. */}
        <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{event.content}</p>
        <button
          onClick={() => setReplying(v => !v)}
          className="text-[10px] font-semibold text-muted-foreground hover:text-primary mt-1"
        >
          {replying ? 'Cancel' : 'Reply'}
        </button>
        {replying && (
          <div className="mt-1.5">
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={`Reply to ${name}…`}
              rows={2}
              maxLength={2000}
              autoFocus
              className="w-full bg-muted rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleReply}
              disabled={!draft.trim() || posting}
              className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {posting ? 'Posting' : 'Reply'}
            </button>
          </div>
        )}
      </div>
      <div className="shrink-0 no-navigate">
        <BCHTipButton
          recipientPubkey={event.pubkey}
          recipientProfile={profile}
          eventId={event.id}
          dropdownAlign="right"
        />
      </div>
    </div>
  );
}

function DiscussionSection({ video }: { video: BCHVideo }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { comments, loading, refetch } = useVideoComments(video);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const handlePost = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    const content = draft.trim();
    if (!content || posting) return;

    setPosting(true);
    setError('');
    const coord = videoCoordinate(video.pubkey, video.id);
    try {
      await publish({
        kind: VIDEO_COMMENT_KIND,
        content,
        tags: [
          // Root scope (uppercase) — the video's addressable coordinate.
          ['A', coord],
          ['K', String(VIDEO_KIND)],
          // Parent (lowercase) — same as the root for a top-level comment.
          ['a', coord],
          ['k', String(VIDEO_KIND)],
          // So the creator is notified of discussion about their video.
          ['p', video.pubkey],
          ['t', VIDEO_COMMENT_TAG],
          ['alt', `Comment on the video "${video.title}"`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setDraft('');
      refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  // Top-level comments first, each followed by its replies, so a thread
  // reads in order without a recursive tree.
  const ordered = useMemo(() => {
    const roots = comments.filter(c => !c.tags.some(t => t[0] === 'e'));
    const repliesByParent = new Map<string, typeof comments>();
    for (const c of comments) {
      const parent = c.tags.find(t => t[0] === 'e')?.[1];
      if (!parent) continue;
      if (!repliesByParent.has(parent)) repliesByParent.set(parent, []);
      repliesByParent.get(parent)!.push(c);
    }
    const out: typeof comments = [];
    for (const root of roots) {
      out.push(root);
      const replies = repliesByParent.get(root.id) || [];
      out.push(...[...replies].sort((a, b) => a.created_at - b.created_at));
    }
    return out;
  }, [comments]);

  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-3 border-y border-border">
        <MessageCircle className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold flex-1">Discussion</h3>
        <span className="text-[11px] text-muted-foreground">
          {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
        </span>
      </div>

      {user ? (
        <div className="px-4 py-3 border-b border-border">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={`What did you think of "${video.title}"?`}
            rows={3}
            maxLength={2000}
            className="w-full bg-muted rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] text-muted-foreground">
              Posted to Nostr as a comment on this video.
            </p>
            <button
              onClick={handlePost}
              disabled={!draft.trim() || posting}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {posting ? 'Posting' : 'Comment'}
            </button>
          </div>
          {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
        </div>
      ) : (
        <div className="px-4 py-4 border-b border-border text-center">
          <button
            onClick={() => setShowLoginDialog(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold"
          >
            Log in to join the discussion
          </button>
        </div>
      )}

      {loading && comments.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : ordered.length === 0 ? (
        <div className="text-center py-10 px-4">
          <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/25 mb-2" />
          <p className="text-xs text-muted-foreground">No comments yet. Start the conversation.</p>
        </div>
      ) : (
        ordered.map(c => (
          <CommentRow key={c.id} event={c} video={video} onReplyPosted={refetch} />
        ))
      )}
    </div>
  );
}

// ── Reaction ──────────────────────────────────────────────────────

function LikeButton({ video }: { video: BCHVideo }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const coord = videoCoordinate(video.pubkey, video.id);
  const [liked, setLiked] = useState(false);

  const { events } = useNostrQuery({
    filters: [{ kinds: [7], '#a': [coord], limit: 300 }],
    allRelays: true,
  });

  const count = useMemo(
    // Re-verify the `a` tag: relays over-return, and a reaction that
    // doesn't actually address this video must not be counted.
    () => new Set(
      events.filter(e => e.tags.some(t => t[0] === 'a' && t[1] === coord)).map(e => e.pubkey),
    ).size,
    [events, coord],
  );
  const alreadyLiked = liked || (!!user && events.some(e => e.pubkey === user.pubkey));

  const handleLike = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (alreadyLiked) return;
    setLiked(true);
    try {
      // A NIP-25 reaction on the addressable coordinate — the same address
      // the comments use, so reactions and comments agree on identity.
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['a', coord],
          ['k', String(VIDEO_KIND)],
          ['p', video.pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {
      setLiked(false);
    }
  };

  return (
    <button
      onClick={handleLike}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
        alreadyLiked
          ? 'border-rose-500/40 bg-rose-500/10 text-rose-500'
          : 'border-border text-muted-foreground hover:text-rose-500 hover:border-rose-500/40'
      }`}
    >
      <Heart className={`w-3.5 h-3.5 ${alreadyLiked ? 'fill-current' : ''}`} />
      {count > 0 ? formatCount(count) : 'Like'}
    </button>
  );
}

// ── Share ─────────────────────────────────────────────────────────

function ShareButton({ video }: { video: BCHVideo }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [state, setState] = useState<'idle' | 'sharing' | 'shared' | 'copied'>('idle');

  const appUrl = `${window.location.origin}${window.location.pathname}`;

  /** Native share sheet on mobile; clipboard elsewhere. */
  const handleQuickShare = async () => {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: video.title, url: appUrl });
        return;
      }
      await navigator.clipboard.writeText(appUrl);
      setState('copied');
      setTimeout(() => setState('idle'), 2500);
    } catch {
      // User dismissed the share sheet, or clipboard is unavailable.
    }
  };

  const handleShareToNostr = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (state !== 'idle') return;
    setState('sharing');
    try {
      await publish({
        kind: 1,
        // Title, the creator's own provider URL, and a link back to the
        // BCHNostr page. Never the media itself.
        content: buildVideoShareText(video, appUrl),
        tags: [
          ['t', 'bchnostr'],
          ['t', 'video'],
          ['a', videoCoordinate(video.pubkey, video.id)],
          ['k', String(VIDEO_KIND)],
          ['p', video.pubkey],
          ['r', appUrl],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setState('shared');
      setTimeout(() => setState('idle'), 3000);
    } catch {
      setState('idle');
    }
  };

  return (
    <div className="flex items-center rounded-full border border-border overflow-hidden">
      <button
        onClick={handleQuickShare}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-primary"
      >
        {state === 'copied' ? <Check className="w-3.5 h-3.5 text-primary" /> : <Share2 className="w-3.5 h-3.5" />}
        {state === 'copied' ? 'Copied' : 'Share'}
      </button>
      <span className="w-px self-stretch bg-border" />
      <button
        onClick={handleShareToNostr}
        disabled={state === 'sharing'}
        title="Post this video to your Nostr feed"
        className="px-2.5 py-2 text-xs font-semibold text-muted-foreground hover:text-primary disabled:opacity-60"
      >
        {state === 'sharing' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : state === 'shared' ? <Check className="w-3.5 h-3.5 text-primary" />
          : 'Post'}
      </button>
    </div>
  );
}

// ── Add to playlist ───────────────────────────────────────────────

function AddToPlaylistButton({ video }: { video: BCHVideo }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { playlists, refetch } = useVideoPlaylists(user?.pubkey);
  const { savePlaylist, publishing } = useVideoPublish();
  const [open, setOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState('');

  const coord = videoCoordinate(video.pubkey, video.id);

  const handleToggle = async (playlistId: string, current: string[]) => {
    setBusy(playlistId);
    const playlist = playlists.find(p => p.id === playlistId);
    if (!playlist) { setBusy(''); return; }
    const next = current.includes(coord)
      ? current.filter(r => r !== coord)
      : [...current, coord];
    try {
      await savePlaylist(playlistId, {
        title: playlist.title,
        description: playlist.description,
        image: playlist.image,
        videoRefs: next,
      });
      refetch();
    } catch {} finally {
      setBusy('');
    }
  };

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    const dTag = playlistDTag(title);
    setBusy(dTag);
    try {
      await savePlaylist(dTag, {
        title,
        description: '',
        image: video.thumbnailUrl,
        videoRefs: [coord],
      });
      setNewTitle('');
      refetch();
    } catch {} finally {
      setBusy('');
    }
  };

  if (!user) {
    return (
      <button
        onClick={() => setShowLoginDialog(true)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
      >
        <ListPlus className="w-3.5 h-3.5" /> Save
      </button>
    );
  }

  return (
    <div className="relative no-navigate">
      <button
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
          open
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:text-primary hover:border-primary/40'
        }`}
      >
        <ListPlus className="w-3.5 h-3.5" /> Save
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 bottom-full mb-2 z-30 w-64 rounded-xl border border-border bg-popover shadow-xl overflow-hidden">
            <p className="px-3 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider border-b border-border">
              Save to playlist
            </p>
            <div className="max-h-48 overflow-y-auto">
              {playlists.length === 0 ? (
                <p className="px-3 py-2.5 text-[11px] text-muted-foreground">
                  No playlists yet. Create one below.
                </p>
              ) : (
                playlists.map(pl => {
                  const included = pl.videoRefs.includes(coord);
                  return (
                    <button
                      key={pl.id}
                      onClick={() => handleToggle(pl.id, pl.videoRefs)}
                      disabled={busy === pl.id || publishing}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 disabled:opacity-60"
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        included ? 'bg-primary border-primary' : 'border-border'
                      }`}>
                        {busy === pl.id
                          ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                          : included ? <Check className="w-2.5 h-2.5 text-primary-foreground" /> : null}
                      </span>
                      <span className="text-xs truncate flex-1">{pl.title}</span>
                      <span className="text-[10px] text-muted-foreground">{pl.videoRefs.length}</span>
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t border-border p-2 flex gap-1.5">
              <input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="New playlist name"
                maxLength={200}
                className="flex-1 min-w-0 bg-muted rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={handleCreate}
                disabled={!newTitle.trim() || publishing}
                className="px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-semibold disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Creator analytics ─────────────────────────────────────────────
//
// Modest and honest. BCHNostr sees page views and Play clicks inside its
// own UI. It cannot see plays on the provider, and it cannot see watch
// time — so it never claims either. External view counts are shown only if
// the provider actually supplies one, which none currently do.

function VideoAnalytics({ video, engagement }: { video: BCHVideo; engagement: VideoEngagement }) {
  const [open, setOpen] = useState(false);
  const views = videoViewCount(video.pubkey, video.id);
  const clicks = playClickCount(video.pubkey, video.id);
  const { boosts } = useVideoBoosts();
  const boostCount = boosts.filter(
    b => b.videoPubkey === video.pubkey && b.videoDTag === video.id,
  ).length;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
      >
        <Eye className="w-4 h-4 text-primary shrink-0" />
        <span className="text-xs font-bold flex-1">Your video analytics</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
          : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-3 space-y-2 border-t border-border">
          <div className="grid grid-cols-2 gap-2">
            <Stat icon={Eye} label="Video page views" value={views} />
            <Stat icon={MousePointerClick} label="Play clicks" value={clicks} />
            <Stat icon={MessageCircle} label="Comments" value={engagement.comments} />
            <Stat icon={Heart} label="Reactions" value={engagement.reactions} />
            <Stat icon={Coins} label="Tips received" value={engagement.uniqueTippers} />
            <Stat icon={Rocket} label="Boosts purchased" value={boostCount} />
          </div>

          {/* The honesty note is not decoration — it's the difference
              between a useful signal and a misleading claim. */}
          <div className="rounded-lg bg-muted/50 border border-border p-2.5 space-y-1.5">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Video page views</strong> and{' '}
              <strong className="text-foreground">play clicks</strong> count what happens
              inside BCHNostr, in this browser. They are{' '}
              <strong className="text-foreground">not</strong> external views and{' '}
              <strong className="text-foreground">not</strong> watch time — your video is
              served by {videoProviderLabel(video)}, so only they can measure that.
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              For real view and watch-time numbers, use your provider's own analytics.
              Comments, reactions and tips are read from Nostr relays and are therefore
              approximate.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-background p-2.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
      </div>
      <p className="text-lg font-bold tabular-nums mt-0.5">{formatCount(value)}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function VideoWatchPage() {
  const { pubkey: rawPubkey, dTag: rawDTag } = useParams<{ pubkey: string; dTag: string }>();
  const pubkey = useMemo(
    () => (rawPubkey && /^[0-9a-f]{64}$/i.test(rawPubkey) ? rawPubkey.toLowerCase() : undefined),
    [rawPubkey],
  );
  const dTag = useMemo(() => {
    if (!rawDTag) return undefined;
    try { return decodeURIComponent(rawDTag); } catch { return rawDTag; }
  }, [rawDTag]);

  const { user } = useAppContext();
  const { video, loading, refetch } = useVideo(pubkey, dTag);
  const { profile } = useNostrProfile(pubkey);
  const { videos: allVideos } = useVideoDirectory();
  const engagement = useSingleVideoEngagement(video);
  const { byVideo: boostsByVideo } = useVideoBoosts();
  const { address: creatorAddress } = useBCHAddress(pubkey);
  const { price: bchPrice } = useBCHPrice();
  const { removeVideo, publishing } = useVideoPublish();

  const [boostOpen, setBoostOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const isOwner = !!user && !!pubkey && user.pubkey === pubkey;
  const boost = video ? boostsByVideo.get(`${video.pubkey}:${video.id}`) : undefined;

  // Record the visit + interest exactly once per video.
  const recordedRef = useRef('');
  useEffect(() => {
    if (!video) return;
    const key = `${video.pubkey}:${video.id}`;
    if (recordedRef.current === key) return;
    recordedRef.current = key;
    recordVideoView(video.pubkey, video.id);
    recordWatch(video);
    recordCategoryInterest(video.category);
  }, [video]);

  const related = useMemo(
    () => (video ? relatedVideos(video, allVideos, 10) : []),
    [video, allVideos],
  );

  const handleDelete = async () => {
    if (!video) return;
    try {
      await removeVideo(video.id);
      refetch();
      setConfirmDelete(false);
    } catch {}
  };

  if (!pubkey || !dTag) {
    return <VideoEmptyState title="Video not found" body="That video link isn’t valid." />;
  }

  if (loading && !video) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!video) {
    return (
      <VideoEmptyState
        title="Video not found"
        body={isOwner
          ? 'This video isn’t published, or you removed it.'
          : 'This video isn’t available. It may have been removed by the creator.'}
        action={{ to: '/videos', label: 'Browse BCH Video' }}
      />
    );
  }

  const name = profile?.display_name || profile?.name || video.pubkey.slice(0, 8);
  const tipAddress = creatorAddress || '';
  const longDescription = video.description.length > 280;

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to="/videos" className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-bold truncate">{video.title}</h1>
          <p className="text-[11px] text-muted-foreground truncate">{name}</p>
        </div>
      </div>

      <div className="lg:flex lg:gap-6 lg:px-4 lg:pt-4">
        {/* ── Main column ── */}
        <div className="lg:flex-1 lg:min-w-0">
          {/* Player — full-bleed on mobile so it uses every pixel. */}
          <div className="sm:px-4 lg:px-0 sm:pt-4 lg:pt-0">
            <VideoPlayer video={video} />
          </div>

          <div className="px-4 lg:px-0 pt-3 space-y-3">
            {/* ── Title & meta ── */}
            <div>
              <div className="flex items-start gap-2">
                <h2 className="text-base sm:text-lg font-extrabold leading-snug flex-1">
                  {video.title}
                </h2>
                {boost && <PromotionBadge boost={boost} />}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                <span>{formatPublished(video.publishedAt)}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                  {videoCategoryLabel(video.category)}
                </span>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-primary"
                  title={`This video is hosted by ${videoProviderLabel(video)}`}
                >
                  <ExternalLink className="w-2.5 h-2.5" /> {videoProviderLabel(video)}
                </a>
                {engagement.uniqueTippers > 0 && (
                  <span className="text-primary font-medium">
                    {engagement.uniqueTippers} tipper{engagement.uniqueTippers === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>

            {/* ── Creator row ── */}
            <div className="flex items-center gap-3 py-3 border-y border-border">
              <Link to={videoChannelPath(video.pubkey)} className="shrink-0">
                {profile?.picture ? (
                  <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10" />
                )}
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={videoChannelPath(video.pubkey)}
                  className="flex items-center gap-1.5 text-sm font-bold hover:text-primary transition-colors"
                >
                  <span className="truncate">{name}</span>
                  <UserBadges pubkey={video.pubkey} size="sm" showTooltip={false} />
                </Link>
                <Link
                  to={videoChannelPath(video.pubkey)}
                  className="text-[11px] text-muted-foreground hover:text-primary"
                >
                  View channel
                </Link>
              </div>
              <div className="shrink-0">
                <FollowButton pubkey={video.pubkey} size="md" />
              </div>
            </div>

            {/* ── Actions ──
                Tip is FIRST and reachable in one tap on mobile: nobody
                should have to navigate through screens to pay a creator. */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Tip is gated by creator verification: only a verified creator
                  can receive tips on a YouTube/Odysee video. The owner sees a
                  "Verify to earn tips" shortcut instead when unverified. */}
              {(tipAddress || isOwner) && (
                <div className="no-navigate">
                  <VideoTipControl
                    video={video}
                    recipientProfile={profile}
                    onVerified={() => refetch()}
                  />
                </div>
              )}
              <button
                onClick={() => setBoostOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-semibold hover:opacity-90"
              >
                <Rocket className="w-3.5 h-3.5" /> Boost
              </button>
              <a
                href="#video-discussion"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Discuss
                {engagement.comments > 0 && ` (${formatCount(engagement.comments)})`}
              </a>
              <LikeButton video={video} />
              <AddToPlaylistButton video={video} />
              <ShareButton video={video} />
              <button
                onClick={() => setShowSupport(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
                  showSupport
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-primary hover:border-primary/40'
                }`}
              >
                <Star className="w-3.5 h-3.5" /> Support
              </button>
              {isOwner && (
                <>
                  <button
                    onClick={() => setEditOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-red-500 hover:border-red-500/40"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </>
              )}
            </div>

            {confirmDelete && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 space-y-2">
                <p className="text-xs font-semibold">Remove this video from BCHNostr?</p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  It disappears from the feeds and your channel. The video itself is
                  untouched on {videoProviderLabel(video)}, and the discussion thread
                  remains on Nostr.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={publishing}
                    className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {publishing && <Loader2 className="w-3 h-3 animate-spin" />} Remove
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 rounded-lg bg-muted text-xs font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Creator economy ──
                The EXISTING membership / goal / crowdfunding rails, not a
                new payment system. Linked, never duplicated. */}
            {showSupport && (
              <div className="rounded-xl border border-border bg-card p-3">
                <CreatorSupportPanel pubkey={video.pubkey} bchPrice={bchPrice} />
              </div>
            )}

            {/* ── Description ── */}
            {video.description && (
              <div className="rounded-xl bg-muted/40 border border-border p-3">
                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${
                  longDescription && !descExpanded ? 'line-clamp-4' : ''
                }`}>
                  {video.description}
                </p>
                {longDescription && (
                  <button
                    onClick={() => setDescExpanded(v => !v)}
                    className="text-[11px] font-semibold text-primary mt-1.5"
                  >
                    {descExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}

            {/* ── Tags ── */}
            {video.hashtags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {video.hashtags.map(tag => (
                  <Link
                    key={tag}
                    to={`/search?q=%23${encodeURIComponent(tag)}`}
                    className="px-2 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground hover:text-primary"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            {/* ── Owner analytics ── */}
            {isOwner && <VideoAnalytics video={video} engagement={engagement} />}

            {/* ── Hosting honesty ── */}
            <div className="flex items-start gap-2 text-[10px] text-muted-foreground/80 leading-relaxed">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <p>
                This video streams directly from {videoProviderLabel(video)}. BCHNostr
                doesn't host, store, or proxy it — only the discussion, tips and boosts
                live here.
              </p>
            </div>
          </div>

          {/* ── Discussion ── */}
          <div id="video-discussion" className="mt-4">
            <DiscussionSection video={video} />
          </div>
        </div>

        {/* ── Related sidebar ── */}
        {related.length > 0 && (
          <aside className="lg:w-[22rem] lg:shrink-0 px-4 lg:px-0 pt-6 lg:pt-0">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
              Related videos
            </h3>
            <div className="space-y-3">
              {related.map(v => (
                <VideoCard
                  key={`${v.pubkey}:${v.id}`}
                  video={v}
                  variant="row"
                  boost={boostsByVideo.get(`${v.pubkey}:${v.id}`)}
                />
              ))}
            </div>
          </aside>
        )}
      </div>

      {boostOpen && (
        <VideoBoostModal
          videoPubkey={video.pubkey}
          videoDTag={video.id}
          videoTitle={video.title}
          onClose={() => setBoostOpen(false)}
        />
      )}
      {editOpen && (
        <AddVideoModal
          editing={video}
          onClose={() => setEditOpen(false)}
          onPublished={() => refetch()}
        />
      )}
    </div>
  );
}
